import jsPDF from "jspdf";
import "jspdf-autotable";
import { COMPANY_ADDRESS_LINES, COMPANY_NAME, COMPANY_PDF_FOOTER } from "@/constants/company";
import { shortTermQuote, SHORT_TERM_PACKAGES, type ShortTermMonths } from "@/lib/shortTermPricing";

interface Params {
  cameras: number;
  imagesPerDay: number;
  months: ShortTermMonths;
  accessories: { name: string; quantity: number; unit_price: number }[];
  quoteNumber?: string | null;
  contact?: { first_name?: string; last_name?: string; email?: string } | null;
  organizationName?: string | null;
}

const money = (n: number) => n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function downloadShortTermQuotePdf(p: Params): Promise<string> {
  return new Promise((resolve) => {
    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const q = shortTermQuote(p.cameras, p.imagesPerDay, p.months);
    const pkg = SHORT_TERM_PACKAGES.find((x) => x.imagesPerDay === p.imagesPerDay)?.name || "Core";
    const accTotal = p.accessories.reduce((s, a) => s + a.quantity * a.unit_price, 0);

    const content = () => {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(28);
      doc.text("QUOTE", pageWidth - 60, 25);
      doc.setFontSize(9);
      let leftY = 40;
      doc.setFont(undefined as any, "bold");
      doc.text(COMPANY_NAME, 20, leftY);
      doc.setFont(undefined as any, "normal");
      COMPANY_ADDRESS_LINES.forEach((l) => doc.text(l, 20, (leftY += 5)));

      const rightX = pageWidth - 80;
      let rightY = 40;
      const fmt = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
      doc.text(`Date: ${fmt(new Date())}`, rightX, rightY);
      doc.text(`Expiry: ${fmt(new Date(Date.now() + 14 * 864e5))}`, rightX, (rightY += 5));
      const quoteNumber = p.quoteNumber || `QU-${new Date().getFullYear()}-PENDING`;
      doc.text(`Quote Number: ${quoteNumber}`, rightX, (rightY += 5));

      let y = rightY + 10;
      doc.setFont(undefined as any, "bold");
      doc.text("Quote Addressed To:", 20, y);
      doc.setFont(undefined as any, "normal");
      const name = `${p.contact?.first_name || ""} ${p.contact?.last_name || ""}`.trim();
      if (name) doc.text(name, 20, (y += 5));
      if (p.organizationName) doc.text(p.organizationName, 20, (y += 5));
      if (p.contact?.email) doc.text(p.contact.email, 20, (y += 5));

      const sy = Math.max(leftY, y) + 15;
      doc.setFont(undefined as any, "bold");
      doc.setFontSize(11);
      doc.text("Order Summary", 20, sy);
      doc.setFontSize(8);
      doc.setFont(undefined as any, "normal");
      doc.text(`Plan: Short Term ${pkg} – ${p.imagesPerDay} images/day per camera`, 20, sy + 8);
      doc.text(`Term: ${p.months} months (${q.termWeeks} weeks)`, 20, sy + 13);
      doc.text(`Contract total images: ${q.totalImages.toLocaleString("en-GB")}`, rightX, sy + 8);
      doc.text(`Weekly cost per camera: £${money(q.weeklyPerCamera)}`, rightX, sy + 13);

      const body: string[][] = [
        [
          `Snapshot camera, Short Term ${pkg} (${p.months} months, camera included)`,
          String(q.cameras),
          `£${money(q.weeklyPerCamera)} / week`,
          `£${money(q.weekly)} / week`,
        ],
        ...p.accessories.map((a) => [a.name, String(a.quantity), `£${money(a.unit_price)}`, `£${money(a.quantity * a.unit_price)}`]),
        ["", "", `Weekly total (${q.cameras} camera${q.cameras === 1 ? "" : "s"}):`, `£${money(q.weekly)}`],
        ["", "", "Contract total images:", q.totalImages.toLocaleString("en-GB")],
        ["", "", "Total:", `£${money(q.contractTotal + accTotal)}`],
      ];
      const lineRows = 1 + p.accessories.length;
      (doc as any).autoTable({
        startY: sy + 20,
        head: [["Description", "Qty", "Unit Price", "Total"]],
        body,
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [240, 240, 240], fontStyle: "bold" },
        columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 20 }, 2: { cellWidth: 40 }, 3: { cellWidth: 35 } },
        didParseCell: (d: any) => {
          if (d.section !== "body" || d.row.index < lineRows) return;
          const label = String(d.row.raw?.[2] ?? "");
          if (label.startsWith("Total") || label.startsWith("Weekly total")) {
            d.cell.styles.fontStyle = "bold";
          }
        },
        margin: { left: 20, right: 20 },
      });

      let cy = (doc as any).lastAutoTable.finalY + 5;
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text("All amounts are ex VAT", 20, cy);
      cy += 8;
      doc.setFillColor(223, 212, 201);
      doc.rect(20, cy, pageWidth - 40, 14, "F");
      doc.setTextColor(50, 21, 33);
      doc.setFontSize(10);
      doc.setFont(undefined as any, "bold");
      doc.text("Agreement Duration", 24, cy + 5);
      doc.setFont(undefined as any, "normal");
      doc.setFontSize(9);
      doc.text(`Agreement duration: ${p.months} months (${q.termWeeks} weeks), commences on delivery.`, 24, cy + 11);
      cy += 20;
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      doc.setFont(undefined as any, "bold");
      doc.text("Basic Terms", 20, cy);
      cy += 8;
      doc.setFont(undefined as any, "normal");
      doc.setFontSize(8);
      [
        `Short Term – Camera${q.cameras > 1 ? "s are" : " is"} included in the price for the ${p.months}-month term and remain${q.cameras > 1 ? "" : "s"} the property of Snapshot Live.`,
        `Images – Up to ${p.imagesPerDay} images per camera per day (${q.totalImages.toLocaleString("en-GB")} images in total over the term). Additional images £0.49 per image.`,
        "Included Features – Pricing includes privacy filtering, AI-driven image search*, and optional real-time notifications.",
        "Image Retention – Images are stored on the Snapshot Live platform for 90 days.",
        "*see full terms and conditions for details",
      ].forEach((t) => {
        const lines = doc.splitTextToSize(t, pageWidth - 40);
        doc.text(lines, 20, cy);
        cy += lines.length * 3.5 + 3;
      });
      doc.text(doc.splitTextToSize(COMPANY_PDF_FOOTER, pageWidth - 40), 20, doc.internal.pageSize.getHeight() - 13);

      const fileName = `Quote_${p.quoteNumber || `TEMP-${Date.now().toString().slice(-4)}`}_${name ? name.replace(/\s+/g, "_") : "Opportunity"}.pdf`;
      doc.save(fileName);
      resolve(fileName);
    };

    const fallback = () => {
      doc.setFontSize(24);
      doc.setFont(undefined as any, "bold");
      doc.setTextColor(50, 21, 33);
      doc.text("SNAPSHOT LIVE", 20, 25);
      content();
    };
    const img = document.createElement("img");
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 480;
        c.height = Math.round(img.height * (480 / img.width));
        const ctx = c.getContext("2d")!;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        doc.addImage(c.toDataURL("image/jpeg", 0.85), "JPEG", 20, 15, 60, (img.height / img.width) * 60, undefined, "FAST");
        content();
      } catch {
        fallback();
      }
    };
    img.onerror = fallback;
    img.src = "/snapshot-logo.png";
  });
}
